import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Save, Loader2, AlertCircle, CheckCircle2,
  Users, MessageSquare, Star, ThumbsUp, MapPin, Cloud, TrendingUp
} from 'lucide-react';
import { vendorApi } from '../services/api';
import { VendorFairMetricsCreate, VendorFairMetricsResponse, WeatherImpact } from '../types';

/**
 * PostFairSurvey - Multi-step survey form for vendors to submit post-fair feedback (Issue #682)
 *
 * Steps:
 * 1. Quantitative Metrics (foot traffic, interactions, leads)
 * 2. Satisfaction Ratings (overall, location, organization)
 * 3. Qualitative Feedback (comments, weather impact, return intent)
 */
export default function PostFairSurvey() {
  const { vendorId, fairId } = useParams<{ vendorId: string; fairId: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [existingMetrics, setExistingMetrics] = useState<VendorFairMetricsResponse | null>(null);

  const [formData, setFormData] = useState<Partial<VendorFairMetricsCreate>>({
    vendor_id: vendorId ? parseInt(vendorId) : 0,
    fair_id: fairId ? parseInt(fairId) : 0,
    foot_traffic_estimate: undefined,
    customer_interactions: undefined,
    business_cards_collected: undefined,
    email_signups: undefined,
    social_media_follows: undefined,
    satisfaction_rating: undefined,
    booth_location_rating: undefined,
    fair_organization_rating: undefined,
    would_return: undefined,
    feedback_text: '',
    weather_impact: undefined,
  });

  // Load existing metrics if already submitted
  useEffect(() => {
    const loadExistingMetrics = async () => {
      if (!vendorId || !fairId) return;

      try {
        setIsLoading(true);
        const metrics = await vendorApi.getVendorFairMetrics(
          parseInt(vendorId),
          parseInt(fairId)
        );
        setExistingMetrics(metrics);
        // Pre-populate form with existing data
        setFormData({
          vendor_id: metrics.vendor_id,
          fair_id: metrics.fair_id,
          foot_traffic_estimate: metrics.foot_traffic_estimate || undefined,
          customer_interactions: metrics.customer_interactions || undefined,
          business_cards_collected: metrics.business_cards_collected || undefined,
          email_signups: metrics.email_signups || undefined,
          social_media_follows: metrics.social_media_follows || undefined,
          satisfaction_rating: metrics.satisfaction_rating || undefined,
          booth_location_rating: metrics.booth_location_rating || undefined,
          fair_organization_rating: metrics.fair_organization_rating || undefined,
          would_return: metrics.would_return || undefined,
          feedback_text: metrics.feedback_text || '',
          weather_impact: metrics.weather_impact as WeatherImpact || undefined,
        });
      } catch (err: unknown) {
        // 404 means no metrics yet - that's fine for new survey
        const error = err as { response?: { status?: number } };
        if (error?.response?.status !== 404) {
          console.error('Error loading existing metrics:', err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingMetrics();
  }, [vendorId, fairId]);

  const handleChange = (field: keyof VendorFairMetricsCreate, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep = (step: number): boolean => {
    // All fields are optional, so validation is minimal
    // Just ensure numeric fields are non-negative if provided
    if (step === 1) {
      const numericFields = [
        'foot_traffic_estimate',
        'customer_interactions',
        'business_cards_collected',
        'email_signups',
        'social_media_follows'
      ];

      for (const field of numericFields) {
        const value = formData[field as keyof VendorFairMetricsCreate];
        if (value !== undefined && value !== null && value < 0) {
          setError(`${field} cannot be negative`);
          return false;
        }
      }
    }

    if (step === 2) {
      const ratingFields = [
        'satisfaction_rating',
        'booth_location_rating',
        'fair_organization_rating'
      ];

      for (const field of ratingFields) {
        const value = formData[field as keyof VendorFairMetricsCreate];
        if (value !== undefined && value !== null && (value < 1 || value > 5)) {
          setError(`${field} must be between 1 and 5`);
          return false;
        }
      }
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    if (!vendorId || !fairId) return;

    try {
      setIsSaving(true);
      setError(null);

      const metricsData: VendorFairMetricsCreate = {
        vendor_id: parseInt(vendorId),
        fair_id: parseInt(fairId),
        ...formData as Omit<VendorFairMetricsCreate, 'vendor_id' | 'fair_id'>,
      };

      if (existingMetrics) {
        // Update existing metrics
        await vendorApi.updateVendorFairMetrics(
          parseInt(vendorId),
          parseInt(fairId),
          metricsData
        );
        setSuccessMessage('Survey updated successfully!');
      } else {
        // Create new metrics
        await vendorApi.submitVendorFairMetrics(
          parseInt(vendorId),
          parseInt(fairId),
          metricsData
        );
        setSuccessMessage('Survey submitted successfully!');
      }

      // Redirect after short delay
      setTimeout(() => {
        navigate(`/vendor/${vendorId}/dashboard`);
      }, 2000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error?.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setIsSaving(false);
    }
  };

  const renderRatingStars = (rating: number | undefined, onChange: (value: number) => void) => {
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(value => (
          <button
            key={value}
            type="button"
            onClick={() => onChange(value)}
            className={`p-2 rounded-lg transition-colors ${
              rating && value <= rating
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-300 hover:text-gray-400'
            }`}
          >
            <Star className={`w-8 h-8 ${rating && value <= rating ? 'fill-current' : ''}`} />
          </button>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-secondary mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-teal-900 mb-2">{successMessage}</h2>
          <p className="text-primary-dark mb-4">Thank you for your valuable feedback!</p>
          <p className="text-sm text-secondary">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {existingMetrics ? 'Update Fair Feedback' : 'Post-Fair Survey'}
        </h1>
        <p className="text-gray-600">
          Help us improve future fairs by sharing your experience
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {['Metrics', 'Ratings', 'Feedback'].map((label, index) => (
            <div key={label} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  currentStep > index + 1
                    ? 'bg-primary text-white'
                    : currentStep === index + 1
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {index + 1}
              </div>
              <span className={`ml-2 text-sm font-medium ${
                currentStep === index + 1 ? 'text-secondary' : 'text-gray-500'
              }`}>
                {label}
              </span>
              {index < 2 && (
                <div className={`flex-1 h-1 mx-2 ${
                  currentStep > index + 1 ? 'bg-primary' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Step 1: Quantitative Metrics */}
      {currentStep === 1 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-secondary" />
              <h2 className="text-xl font-bold text-gray-900">Quantitative Metrics</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Help us understand the engagement at your booth. All fields are optional.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Estimated Foot Traffic
              </label>
              <input
                type="number"
                min="0"
                value={formData.foot_traffic_estimate || ''}
                onChange={(e) => handleChange('foot_traffic_estimate', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 150"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-500 mt-1">Estimated visitors to your booth</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-1" />
                Customer Interactions
              </label>
              <input
                type="number"
                min="0"
                value={formData.customer_interactions || ''}
                onChange={(e) => handleChange('customer_interactions', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 45"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-500 mt-1">Meaningful conversations</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Cards Collected
              </label>
              <input
                type="number"
                min="0"
                value={formData.business_cards_collected || ''}
                onChange={(e) => handleChange('business_cards_collected', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 20"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Signups
              </label>
              <input
                type="number"
                min="0"
                value={formData.email_signups || ''}
                onChange={(e) => handleChange('email_signups', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 12"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Social Media Follows
              </label>
              <input
                type="number"
                min="0"
                value={formData.social_media_follows || ''}
                onChange={(e) => handleChange('social_media_follows', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 8"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Satisfaction Ratings */}
      {currentStep === 2 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-secondary" />
              <h2 className="text-xl font-bold text-gray-900">Satisfaction Ratings</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Rate your experience on a scale of 1 to 5 stars
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Overall Satisfaction
              </label>
              {renderRatingStars(formData.satisfaction_rating, (value) => handleChange('satisfaction_rating', value))}
              <p className="text-xs text-gray-500 mt-2">How satisfied were you with the fair overall?</p>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <MapPin className="w-4 h-4 inline mr-1" />
                Booth Location Quality
              </label>
              {renderRatingStars(formData.booth_location_rating, (value) => handleChange('booth_location_rating', value))}
              <p className="text-xs text-gray-500 mt-2">How would you rate your booth location?</p>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Fair Organization Quality
              </label>
              {renderRatingStars(formData.fair_organization_rating, (value) => handleChange('fair_organization_rating', value))}
              <p className="text-xs text-gray-500 mt-2">How well was the fair organized?</p>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Qualitative Feedback */}
      {currentStep === 3 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-secondary" />
              <h2 className="text-xl font-bold text-gray-900">Additional Feedback</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Share your thoughts and help us improve
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ThumbsUp className="w-4 h-4 inline mr-1" />
                Would you return to this fair?
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handleChange('would_return', true)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                    formData.would_return === true
                      ? 'border-primary bg-teal-50 text-primary-dark'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('would_return', false)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                    formData.would_return === false
                      ? 'border-red-600 bg-red-50 text-red-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Cloud className="w-4 h-4 inline mr-1" />
                Weather Impact
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['positive', 'neutral', 'negative'] as WeatherImpact[]).map((impact) => (
                  <button
                    key={impact}
                    type="button"
                    onClick={() => handleChange('weather_impact', impact)}
                    className={`py-2 px-4 rounded-lg border-2 font-medium transition-colors capitalize ${
                      formData.weather_impact === impact
                        ? 'border-primary bg-teal-50 text-primary-dark'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {impact}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">How did weather affect the fair?</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments & Suggestions
              </label>
              <textarea
                value={formData.feedback_text || ''}
                onChange={(e) => handleChange('feedback_text', e.target.value)}
                placeholder="Share your thoughts, suggestions for improvement, or anything else you'd like us to know..."
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-500 mt-1">Your feedback helps us create better experiences</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 1}
          className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          Previous
        </button>

        {currentStep < 3 ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Next
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Submit Survey
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
